import { useMemo, useState } from 'react';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { ActiveCard } from './components/ActiveCard.jsx';
import { ActiveRow } from './components/ActiveRow.jsx';
import { ToolbarFilters } from './components/ToolbarFilters.jsx';
import { useActiveSessions, useActiveSearch, useDebouncedValue } from './hooks/useActiveSessions.js';

const isOverstay = (s) => s.status === 'expired' || s.status === 'overstay';
const hasPending = (s) => (s.pendingAmount || 0) > 0 || s.hasPendingInvoices;

export default function ActiveChildren() {
  const [view, setView] = useState('grid');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  const term = useDebouncedValue(q.trim());
  const searching = term.length > 0;

  const { data: sessions = [], isLoading, error } = useActiveSessions();
  // البحث يُنفَّذ على الخادم: اسم الطفل، اسم ولي الأمر، هاتفه، ورقم الكارت —
  // مع تجاهل فروق الإملاء العربية والأرقام العربية-الهندية. البحث باسم ولي
  // الأمر يُرجع كل أطفاله الموجودين الآن، لا الأطفال المعروضين في الصفحة فقط.
  const search = useActiveSearch(term);

  // القائمة الأساس: نتيجة البحث حين يكون هناك بحث، وإلا كل الموجودين.
  const base = searching ? (search.data || []) : sessions;

  const counts = useMemo(() => ({
    all: base.length,
    overstay: base.filter(isOverstay).length,
    pending: base.filter(hasPending).length
  }), [base]);

  // الفلاتر تُطبَّق فوق نتيجة البحث فيتراكبان معاً.
  const filtered = useMemo(() => base.filter((s) => {
    if (filter === 'overstay' && !isOverstay(s)) return false;
    if (filter === 'pending' && !hasPending(s)) return false;
    return true;
  }), [base, filter]);

  const busy = searching
    ? (search.isLoading || (search.isFetching && !search.data))
    : isLoading;
  const failed = searching ? search.isError : !!error;

  return (
    <div className="space-y-3.5">
      <ToolbarFilters
        q={q}
        setQ={setQ}
        filter={filter}
        setFilter={setFilter}
        view={view}
        setView={setView}
        counts={counts}
        searching={searching && search.isFetching}
      />

      {failed ? (
        <Card>
          <div className="text-red text-center py-4">
            {searching ? 'تعذّر تنفيذ البحث' : 'تعذّر تحميل الجلسات النشطة'}
          </div>
        </Card>
      ) : null}

      {busy ? (
        <Card>
          <div className="text-ink-3 text-center py-8">{searching ? 'جاري البحث...' : 'جاري التحميل...'}</div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-8 flex flex-col items-center gap-2" data-testid="active-empty">
            <Icon name="children" size={42} color="#D6CCB3" />
            {searching && base.length === 0 ? (
              <>
                <div>لا توجد نتائج مطابقة لبحثك</div>
                <div className="text-[12px]">جرّب اسم الطفل أو اسم ولي الأمر أو رقم هاتفه أو رقم الكارت</div>
              </>
            ) : (
              <div>لا توجد جلسات مطابقة</div>
            )}
          </div>
        </Card>
      ) : view === 'grid' ? (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
          data-testid="active-grid"
        >
          {filtered.map((s) => (
            <ActiveCard key={s._id || s.id} session={s} />
          ))}
        </div>
      ) : (
        <Card>
          <table className="w-full text-[13px]">
            <thead className="text-ink-2 text-[12px]">
              <tr className="border-b border-line">
                <th className="p-2.5 text-start">الطفل</th>
                <th className="p-2.5 text-start">ولي الأمر</th>
                <th className="p-2.5 text-start">الكارت</th>
                <th className="p-2.5 text-start">المدة</th>
                <th className="p-2.5 text-start">التسعير</th>
                <th className="p-2.5 text-start">المبلغ</th>
                <th className="p-2.5 text-start">معلق</th>
                <th className="p-2.5 text-start">الحالة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <ActiveRow key={s._id || s.id} session={s} />
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
